import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useMetricsSectionPreference } from '../hooks/useMetricsSectionPreference';
import type { CockpitMetricView } from '../model/cockpitViewModel';
import { resolveMetricContextNotice } from '../model/cockpitPresentationPolicy';
import { ContextualNotice } from './ContextualNotice';
import { useCockpitStyles } from './cockpitStyles';

const MetricCard = ({
  metric,
  sectionStale,
}: {
  metric: CockpitMetricView;
  sectionStale: boolean;
}) => {
  const classes = useCockpitStyles();
  const notice = resolveMetricContextNotice(metric, sectionStale);
  return (
    <article className={classes.metric} aria-labelledby={`${metric.id}-label`}>
      <Typography
        id={`${metric.id}-label`}
        component="h3"
        className={classes.metricLabel}
      >
        {metric.label}
      </Typography>
      <div className={classes.metricValueRow}>
        <Typography component="p" className={classes.metricValue}>
          {metric.displayValue}
        </Typography>
        {metric.unit && (
          <Typography
            component="span"
            variant="body2"
            className={classes.metricUnit}
          >
            {metric.unit}
          </Typography>
        )}
      </div>
      {notice && (
        <ContextualNotice
          label={`Aviso sobre ${metric.label}`}
          notice={notice}
        />
      )}
    </article>
  );
};

const MetricsGrid = ({
  metrics,
  stale,
}: {
  metrics: CockpitMetricView[];
  stale: boolean;
}) => {
  const classes = useCockpitStyles();
  return (
    <div className={classes.metricGrid}>
      {metrics.map(metric => (
        <MetricCard metric={metric} sectionStale={stale} key={metric.id} />
      ))}
    </div>
  );
};

export const MetricsSection = ({
  metrics,
  stale = false,
  preferenceScope,
}: {
  metrics: CockpitMetricView[];
  stale?: boolean;
  preferenceScope?: string | null;
}) => {
  const classes = useCockpitStyles();
  const preference = useMetricsSectionPreference(preferenceScope);
  return (
    <Accordion
      component="section"
      className={classes.panel}
      aria-labelledby="metrics-title"
      expanded={preference.expanded}
      onChange={(_, expanded) => preference.setExpanded(expanded)}
    >
      <AccordionSummary
        id="metrics-header"
        aria-controls="metrics-content"
        className={classes.metricsSummary}
        classes={{
          content: classes.metricsSummaryContent,
          expanded: classes.metricsExpanded,
        }}
        expandIcon={<ExpandMoreIcon />}
      >
        <Typography
          id="metrics-title"
          component="h2"
          className={classes.sectionTitle}
        >
          Métricas
        </Typography>
      </AccordionSummary>
      <AccordionDetails id="metrics-content" className={classes.metricsDetails}>
        <MetricsGrid metrics={metrics} stale={stale} />
      </AccordionDetails>
    </Accordion>
  );
};
